import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { Notification } from '../../../models/notification.models';
import { NotificationService } from '../../../services/notification.service';
import { resolveNotificationContent } from '../../../utils/mention.utils';

@Component({
  selector: 'app-notification-list',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
})
export class NotificationListComponent implements OnInit {
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  readonly paginator = viewChild(MatPaginator);
  readonly sort = viewChild(MatSort);

  displayedColumns: string[] = ['title', 'message', 'createdAt'];
  dataSource = new MatTableDataSource<Notification>([]);
  pageSize = 5;

  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const paginator = this.paginator();
      if (paginator) this.dataSource.paginator = paginator;
    });
    effect(() => {
      const sort = this.sort();
      if (sort) this.dataSource.sort = sort;
    });
  }

  get hasUnread(): boolean {
    return this.dataSource.data.some((n) => !n.isRead);
  }

  ngOnInit(): void {
    this.notificationService.notifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notifications) => {
        this.dataSource.data = notifications;
      });
  }

  onRowClick(notification: Notification): void {
    if (notification.isRead) return;
    this.markNotificationAsRead(notification);
  }

  getDisplayMessage(notification: Notification): string {
    return resolveNotificationContent(
      notification.message,
      notification.notificationType,
      notification.mentions,
    );
  }

  onMessageClick(event: MouseEvent, notification: Notification): void {
    event.stopPropagation();
    this.markNotificationAsRead(notification);

    const target = event.target as HTMLElement;
    if (target.classList.contains('mention-link')) {
      event.preventDefault();
      const href = target.getAttribute('href');
      if (href) {
        this.router.navigateByUrl(href);
      }
    }
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe();
  }

  private markNotificationAsRead(notification: Notification) {
    this.notificationService.markAsRead(notification.id).subscribe();
  }
}
